(() => {
  'use strict';

  const SELECTOR = '[data-pc-cart-progress]';
  let refreshTimer;
  let activeRequest;

  const containsPreorder = (value) => /pre[\s-]?order/i.test(String(value || ''));

  const isEligiblePreorderItem = (item) => {
    const properties = item?.properties || {};
    const hasEligibleMarker = ['true', '1', 'yes'].includes(
      String(properties._pc_discount_eligible || '').toLowerCase()
    );
    const hasPreorderProperty = Object.values(properties).some(containsPreorder);
    const hasPreorderOption = (item?.options_with_values || []).some((option) =>
      containsPreorder(option?.value)
    );

    return hasEligibleMarker || hasPreorderProperty || hasPreorderOption;
  };

  const getProgressState = (mealCount) => {
    if (mealCount >= 12) {
      return {
        unlocked: 10,
        message: 'You unlocked 10% off eligible pre-order meals.'
      };
    }

    const nextGoal = mealCount >= 10 ? 12 : mealCount >= 8 ? 10 : 8;
    const nextDiscount = mealCount >= 10 ? 10 : mealCount >= 8 ? 8 : 6;
    const unlocked = mealCount >= 10 ? 8 : mealCount >= 8 ? 6 : 0;
    const remaining = Math.max(nextGoal - mealCount, 0);
    const mealWord = remaining === 1 ? 'meal' : 'meals';
    const prefix = unlocked ? `${unlocked}% unlocked. ` : '';

    return {
      unlocked,
      message: `${prefix}Add ${remaining} more ${mealWord} for ${nextDiscount}% off.`
    };
  };

  const updateProgress = (mealCount) => {
    const count = Math.max(Number(mealCount) || 0, 0);
    const progressCount = Math.min(count, 12);
    const progressPercent = `${(progressCount / 12) * 100}%`;
    const state = getProgressState(count);

    document.querySelectorAll(SELECTOR).forEach((progress) => {
      progress.hidden = count === 0;
      progress.style.setProperty('--pc-cart-progress', progressPercent);

      const countLabel = progress.querySelector('[data-pc-progress-count]');
      if (countLabel) {
        countLabel.textContent = `${count} pre-order ${count === 1 ? 'meal' : 'meals'}`;
      }

      const status = progress.querySelector('[data-pc-progress-status]');
      if (status) {
        status.textContent = state.unlocked ? `${state.unlocked}% unlocked` : 'Build your discount';
      }

      const track = progress.querySelector('[role="progressbar"]');
      track?.setAttribute('aria-valuenow', String(progressCount));

      const ticker = progress.querySelector('[data-pc-progress-ticker]');
      if (ticker) ticker.textContent = String(progressCount);

      progress.querySelectorAll('[data-pc-progress-threshold]').forEach((threshold) => {
        threshold.classList.toggle('is-reached', count >= Number(threshold.dataset.pcProgressThreshold));
      });

      const message = progress.querySelector('[data-pc-progress-message]');
      if (message) message.textContent = state.message;
    });
  };

  const getCartUrl = () => {
    const cartPath = window.theme?.routes?.cart_url || '/cart';
    return `${cartPath.replace(/\/$/, '')}.js`;
  };

  const refreshProgress = async () => {
    const renderedCartItems = Array.from(document.querySelectorAll('[data-pc-discount-eligible]'));
    if (renderedCartItems.length) {
      const renderedCount = renderedCartItems.reduce((total, item) => {
        if (item.dataset.pcDiscountEligible !== 'true') return total;
        const quantity = Number(item.querySelector('[data-pc-line-quantity]')?.value || 0);
        return total + quantity;
      }, 0);
      updateProgress(renderedCount);
      return;
    }

    activeRequest?.abort();
    activeRequest = new AbortController();

    try {
      const response = await fetch(`${getCartUrl()}?pc_progress=${Date.now()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: activeRequest.signal
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

      const cart = await response.json();
      const qualifyingCount = (cart.items || []).reduce(
        (total, item) => total + (isEligiblePreorderItem(item) ? Number(item.quantity || 0) : 0),
        0
      );
      updateProgress(qualifyingCount);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Unable to refresh the pre-order discount progress', error);
      }
    }
  };

  const scheduleRefresh = (delay = 80) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshProgress, delay);
  };

  document.addEventListener('DOMContentLoaded', () => scheduleRefresh(0));
  document.addEventListener('theme:cart:change', () => scheduleRefresh());
  document.addEventListener('theme:product:add', () => scheduleRefresh(180));
  document.addEventListener('theme:cart:update', () => scheduleRefresh(350));
})();
